
(() => {
  'use strict';

  const SELECTOR = '[data-mbb-destinations-slider]';
  const SWIPE_THRESHOLD = 8;
  const SWIPE_DISTANCE = 50;

  class MbbDestinationsSlider {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-slider-viewport]');
      this.track = section.querySelector('[data-slider-track]');
      this.slides = [...section.querySelectorAll('[data-slider-slide]')];
      this.prev = section.querySelector('[data-slider-prev]');
      this.next = section.querySelector('[data-slider-next]');
      this.pagination = section.querySelector('[data-slider-pagination]');
      this.navigation = section.querySelector('.mbb-destinations-slider__navigation');

      if (!this.viewport || !this.track || !this.slides.length) return;

      this.index = 0;
      this.visible = 3;
      this.maxIndex = 0;

      this.startX = 0;
      this.startY = 0;
      this.deltaX = 0;
      this.pointerId = null;
      this.dragging = false;
      this.hasDragged = false;
      this.blockClick = false;
      this.resizeTimer = null;

      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onDragStart = this.onDragStart.bind(this);

      this.onPrevClick = () => this.goTo(this.index - 1);
      this.onNextClick = () => this.goTo(this.index + 1);

      this.init();
    }

    init() {
      this.section.dataset.sliderInitialized = 'true';
      this.section.setAttribute('role', 'region');
      this.section.setAttribute('aria-roledescription', 'carousel');

      this.bindEvents();
      this.updateLayout();
    }

    getVisibleSlides() {
      if (window.innerWidth <= 749) return 1;
      if (window.innerWidth <= 990) return 2;
      return 3;
    }

    bindEvents() {
      this.prev?.addEventListener('click', this.onPrevClick);
      this.next?.addEventListener('click', this.onNextClick);

      this.viewport.addEventListener('keydown', this.onKeydown);

      this.viewport.addEventListener(
        'pointerdown',
        this.onPointerDown
      );

      this.viewport.addEventListener(
        'pointermove',
        this.onPointerMove
      );

      this.viewport.addEventListener(
        'pointerup',
        this.onPointerUp
      );

      this.viewport.addEventListener(
        'pointercancel',
        this.onPointerCancel
      );

      this.viewport.addEventListener(
        'click',
        this.onClickCapture,
        true
      );

      this.viewport.addEventListener(
        'dragstart',
        this.onDragStart
      );

      window.addEventListener(
        'resize',
        this.onResize,
        { passive: true }
      );

      this.viewport.tabIndex = 0;
      this.viewport.style.touchAction = 'pan-y';
    }

    updateLayout() {
      this.visible = this.getVisibleSlides();
      this.maxIndex = Math.max(
        0,
        this.slides.length - this.visible
      );

      this.index = Math.min(this.index, this.maxIndex);

      this.buildPagination();
      this.update();
    }

    getStep() {
      if (this.slides.length < 2) {
        return this.slides[0]?.offsetWidth || 0;
      }

      const first = this.slides[0].getBoundingClientRect();
      const second = this.slides[1].getBoundingClientRect();

      return second.left - first.left;
    }

    update() {
      const offset = this.index * this.getStep();

      this.track.style.transform =
        `translate3d(${-offset}px, 0, 0)`;

      this.prev?.toggleAttribute(
        'disabled',
        this.index === 0
      );

      this.next?.toggleAttribute(
        'disabled',
        this.index === this.maxIndex
      );

      if (this.navigation) {
        this.navigation.hidden = this.maxIndex === 0;
      }

      this.slides.forEach((slide, position) => {
        const visible =
          position >= this.index &&
          position < this.index + this.visible;

        slide.setAttribute(
          'aria-hidden',
          String(!visible)
        );

        slide.querySelectorAll('a, button').forEach(element => {
          element.tabIndex = visible ? 0 : -1;
        });
      });

      this.pagination
        ?.querySelectorAll('button')
        .forEach((dot, position) => {
          const active = position === this.index;

          dot.classList.toggle('is-active', active);

          dot.setAttribute(
            'aria-current',
            active ? 'true' : 'false'
          );
        });
    }

    goTo(index) {
      const nextIndex = Math.max(
        0,
        Math.min(index, this.maxIndex)
      );

      if (nextIndex === this.index) {
        this.update();
        return;
      }

      this.index = nextIndex;
      this.update();
    }

    buildPagination() {
      if (!this.pagination) return;

      this.pagination.replaceChildren();

      for (
        let index = 0;
        index <= this.maxIndex;
        index += 1
      ) {
        const button = document.createElement('button');

        button.type = 'button';

        button.setAttribute(
          'aria-label',
          `${this.pagination.dataset.paginationItemLabel} ${index + 1}`
        );

        button.addEventListener('click', () => {
          this.goTo(index);
        });

        this.pagination.appendChild(button);
      }
    }

    onKeydown(event) {
      // Ne pas intercepter les touches dans les liens.
      if (event.target.closest('a, button')) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.goTo(this.index - 1);
          break;

        case 'ArrowRight':
          event.preventDefault();
          this.goTo(this.index + 1);
          break;

        case 'Home':
          event.preventDefault();
          this.goTo(0);
          break;

        case 'End':
          event.preventDefault();
          this.goTo(this.maxIndex);
          break;
      }
    }

    onPointerDown(event) {
      if (event.isPrimary === false) return;

      if (
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) return;

      // Les boutons de navigation ne déclenchent jamais de swipe.
      if (event.target.closest('button')) return;

      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startY = event.clientY;

      this.deltaX = 0;
      this.dragging = true;
      this.hasDragged = false;
      this.blockClick = false;

      // Ne pas utiliser setPointerCapture ici :
      // on préserve le clic natif sur les liens.
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      if (event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;

      // Laisser le navigateur gérer le scroll vertical.
      if (
        !this.hasDragged &&
        Math.abs(deltaY) > Math.abs(deltaX) &&
        Math.abs(deltaY) > SWIPE_THRESHOLD
      ) {
        this.resetPointer();
        return;
      }

      // Ne démarrer le swipe qu'après un mouvement horizontal.
      if (!this.hasDragged) {
        if (
          Math.abs(deltaX) < SWIPE_THRESHOLD ||
          Math.abs(deltaX) <= Math.abs(deltaY)
        ) {
          return;
        }

        this.hasDragged = true;
        this.track.style.transition = 'none';
      }

      this.deltaX = deltaX;

      const offset = this.index * this.getStep();

      const resistance =
        (this.index === 0 && deltaX > 0) ||
        (this.index === this.maxIndex && deltaX < 0)
          ? 0.25
          : 1;

      this.track.style.transform = `translate3d(${
        -offset + deltaX * resistance
      }px, 0, 0)`;
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      if (event.pointerId !== this.pointerId) return;

      const didDrag = this.hasDragged;
      const distance = this.deltaX;

      this.dragging = false;

      this.restoreTransition();

      if (didDrag) {
        // Bloquer uniquement le clic synthétique du swipe.
        this.blockClick = true;

        const threshold = Math.min(
          SWIPE_DISTANCE,
          this.viewport.offsetWidth * 0.12
        );

        if (Math.abs(distance) > threshold) {
          this.goTo(
            this.index + (distance < 0 ? 1 : -1)
          );
        } else {
          this.update();
        }
      } else {
        // Tap normal : laisser le lien naviguer.
        this.blockClick = false;
        this.update();
      }

      this.pointerId = null;
      this.deltaX = 0;
      this.hasDragged = false;
    }

    onPointerCancel(event) {
      if (event.pointerId !== this.pointerId) return;

      this.restoreTransition();
      this.update();
      this.resetPointer();
    }

    onClickCapture(event) {
      if (!this.blockClick) return;

      // Ne jamais bloquer une activation clavier.
      if (event.detail === 0) return;

      event.preventDefault();
      event.stopPropagation();

      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }

      this.blockClick = false;
    }

    onDragStart(event) {
      // Empêcher le drag natif des images,
      // sans modifier les clics.
      if (event.target.closest('a, img')) {
        event.preventDefault();
      }
    }

    restoreTransition() {
      this.track.style.transition = this.reducedMotion
        ? 'none'
        : 'transform .55s cubic-bezier(.22,1,.36,1)';
    }

    resetPointer() {
      this.pointerId = null;
      this.dragging = false;
      this.hasDragged = false;
      this.deltaX = 0;
    }

    onResize() {
      clearTimeout(this.resizeTimer);

      this.resizeTimer = setTimeout(() => {
        this.updateLayout();
      }, 120);
    }

    destroy() {
      clearTimeout(this.resizeTimer);

      this.prev?.removeEventListener('click', this.onPrevClick);
      this.next?.removeEventListener('click', this.onNextClick);

      this.viewport.removeEventListener(
        'keydown',
        this.onKeydown
      );

      this.viewport.removeEventListener(
        'pointerdown',
        this.onPointerDown
      );

      this.viewport.removeEventListener(
        'pointermove',
        this.onPointerMove
      );

      this.viewport.removeEventListener(
        'pointerup',
        this.onPointerUp
      );

      this.viewport.removeEventListener(
        'pointercancel',
        this.onPointerCancel
      );

      this.viewport.removeEventListener(
        'click',
        this.onClickCapture,
        true
      );

      this.viewport.removeEventListener(
        'dragstart',
        this.onDragStart
      );

      window.removeEventListener(
        'resize',
        this.onResize
      );

      this.pagination?.replaceChildren();

      delete this.section.dataset.sliderInitialized;
      delete this.section.mbbSlider;
    }
  }

  const init = root => {
    const sections = [];

    if (root.matches?.(SELECTOR)) {
      sections.push(root);
    }

    root.querySelectorAll?.(SELECTOR).forEach(section => {
      sections.push(section);
    });

    sections.forEach(section => {
      if (section.dataset.sliderInitialized === 'true') return;

      section.mbbSlider = new MbbDestinationsSlider(section);
    });
  };

  const start = () => init(document);

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', event => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', event => {
    const sections = [];

    if (event.target.matches?.(SELECTOR)) {
      sections.push(event.target);
    }

    event.target.querySelectorAll?.(SELECTOR).forEach(section => {
      sections.push(section);
    });

    sections.forEach(section => {
      section.mbbSlider?.destroy();
    });
  });
})();
